import { useEffect, useId, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useKruimelNamen } from '@/stores/kruimelNamenStore';
import { FeedbackRapport } from '../components/FeedbackRapport';
import { Geweigerd } from '../components/MeasurementState';
import { MedewerkerDetails } from '../components/MedewerkerDetails';
import { PeriodPicker } from '../components/PeriodPicker';
import { FOCUSRING } from '../focus';
import { useCollegaWeek } from '../hooks/useCollegaWeek';
import { useGekozenWeek } from '../hooks/useGekozenWeek';
import { metMeereizend, WERKKWALITEIT_MEDEWERKERS } from '../tabs';
import { weeknummer } from '../week';

/**
 * Het medewerkersnummer uit het adres, of `null`.
 *
 * Dezelfde vorm die de portaalproxy en de dienst accepteren: cijfers, geen voorloopnul, groter dan
 * nul. Aan de rand geweigerd, zodat een typefout in het adres geen aanroep kost en niet als een
 * storing van de meting op het scherm komt.
 */
export function collegaNummer(ruw: string | undefined): number | null {
  if (typeof ruw !== 'string' || !/^[1-9]\d*$/.test(ruw)) return null;
  const nummer = Number(ruw);
  return Number.isSafeInteger(nummer) ? nummer : null;
}

/** Een naam uit het antwoord, of `null` als hij ontbreekt of leeg is na `trim`. */
function gevuld(naam: unknown): string | null {
  return typeof naam === 'string' && naam.trim() !== '' ? naam.trim() : null;
}

const TEKEN_OPEN = '▸';
const TEKEN_TERUG = '←';

/**
 * Het Zo-ziet-kader en Details van één medewerker. Krijgt `key={userId}` van de pagina: bij een andere
 * medewerker gaan Details weer dicht en staat het kader weer op Week.
 */
function Medewerkerinhoud({ nummer, week, weken, naam }: { nummer: number; week: string; weken: string[]; naam: string | null }) {
  const { t } = useTranslation();
  const kaderKopId = useId();
  const detailsId = useId();
  const detailsKopId = useId();
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Hetzelfde verzoek (zelfde querysleutel) als in FeedbackRapport: één verzoek per week voor kader én Details.
  const { data, isLoading, isError } = useCollegaWeek(nummer, week);
  const persoonsweek = !isLoading && !isError ? (data?.data ?? null) : null;
  const bron = { soort: 'collega', userId: nummer } as const;

  return (
    <>
      {/* Exact het scherm van de medewerker (S3/S4), in de tweede persoon en zonder nakijkernamen (M16),
          en zonder eigen Weekkiezer (die staat in de Medewerkerkop). */}
      <section aria-labelledby={kaderKopId} className="flex min-w-0 flex-col rounded-[12px] border border-[var(--border-strong)]">
        <div className="flex flex-col gap-1 rounded-t-[12px] bg-[var(--primary-light)] px-5 py-3 text-[var(--primary-text)]">
          <h3 className="text-[0.9375rem] font-semibold" id={kaderKopId}>
            {naam === null ? t('qa.medewerker.zoZietKopZonderNaam') : t('qa.medewerker.zoZietKop', { naam })}
          </h3>
          <p className="text-[0.75rem]">
            {naam === null ? t('qa.medewerker.zoZietUitlegZonderNaam') : t('qa.medewerker.zoZietUitleg', { naam })}
          </p>
        </div>
        <div className="flex min-w-0 flex-col gap-4 p-5 max-sm:p-4">
          <FeedbackRapport
            bron={bron}
            kopNiveau={4}
            tabsLabel={naam === null ? t('qa.medewerker.tabsLabelZonderNaam') : t('qa.medewerker.tabsLabel', { naam })}
            week={week}
            weken={weken}
          />
        </div>
      </section>

      {/* Details bestaan pas als er een week is; laden, een storing en een week zonder rapport toont het kader. */}
      {persoonsweek !== null && (
        <section aria-labelledby={detailsKopId} className="rounded-[12px] border border-[var(--border-soft)] bg-[var(--background)]">
          <h3 className="text-[0.9375rem] font-semibold text-[var(--foreground-heading)]">
            <button
              aria-controls={detailsId}
              aria-expanded={detailsOpen}
              className={`flex min-h-[2.75rem] w-full items-center gap-2 rounded-[12px] px-5 py-3 text-left ${FOCUSRING}`}
              onClick={() => setDetailsOpen(!detailsOpen)}
              type="button"
            >
              <span aria-hidden="true" className={`inline-block text-[var(--foreground-muted)] ${detailsOpen ? 'rotate-90' : ''}`}>
                {TEKEN_OPEN}
              </span>
              <span id={detailsKopId}>{t('qa.medewerker.detailsKop', { week: weeknummer(week) })}</span>
            </button>
          </h3>
          <div className="px-5 pb-5 max-sm:px-4" data-testid="werkkwaliteit-details-inhoud" hidden={!detailsOpen} id={detailsId}>
            {detailsOpen && <MedewerkerDetails week={persoonsweek} weekKey={week} />}
          </div>
        </section>
      )}
    </>
  );
}

/**
 * De medewerkerpagina (EXPERIENCE.md S2, variant A): Medewerkerkop met terug naar Team, de naam als kop en de
 * Weekkiezer die de hele pagina stuurt; daaronder "Zo ziet {voornaam} het" met exact het rapport van Mijn feedback
 * voor deze medewerker, en "Details voor de leidinggevende", standaard dicht.
 */
export function EmployeeDetailPage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { search } = useLocation();
  const nummer = collegaNummer(userId);
  const { week, weken, kies } = useGekozenWeek();
  const { data, isGeweigerd } = useCollegaWeek(nummer, week);
  const geweigerd = nummer === null || isGeweigerd === true;

  // De naam van de laatst getoonde week van déze medewerker blijft staan terwijl een andere week laadt of geen
  // rapport heeft: het is dezelfde persoon. Bijgewerkt tijdens het renderen (afgeleide toestand), per userId.
  const [bekend, setBekend] = useState<{ userId?: string; naam?: string; voornaam?: string }>({});
  const actueel = data?.data;
  if (actueel && (bekend.userId !== userId || bekend.naam !== actueel.naam || bekend.voornaam !== actueel.voornaam)) {
    setBekend({ userId, naam: actueel.naam, voornaam: actueel.voornaam });
  }
  const bron = actueel ?? (bekend.userId === userId ? bekend : undefined);
  const volledig = gevuld(bron?.naam);
  const naam = gevuld(bron?.voornaam) ?? volledig;
  // Het kruimelpad toont de naam in plaats van het medewerkersnummer, zodra die bekend is.
  const onthoud = useKruimelNamen((stand) => stand.onthoud);
  useEffect(() => {
    if (userId !== undefined && volledig !== null) onthoud(userId, volledig);
  }, [onthoud, userId, volledig]);
  const terug = `${metMeereizend(WERKKWALITEIT_MEDEWERKERS, search)}${nummer === null ? '' : `#medewerker-${nummer}`}`;

  return (
    <div className="flex min-w-0 flex-col gap-5" data-testid="werkkwaliteit-persoon">
      <div className="flex min-w-0 flex-col gap-3">
        <Link
          className={`inline-flex items-center gap-1 self-start rounded-[4px] text-[0.8125rem] font-medium text-[var(--secondary-text)] underline underline-offset-2 max-sm:min-h-[2.75rem] [@media(pointer:coarse)]:min-h-[2.75rem] ${FOCUSRING}`}
          data-testid="werkkwaliteit-persoon-terug"
          to={terug}
        >
          <span aria-hidden="true">{TEKEN_TERUG}</span>
          {t('qa.medewerker.terug')}
        </Link>
        {/* Eén `h1` per pagina: de schil (WorkQualityLayout) heeft "Werkkwaliteit"; de naam is daarom `h2`, en
            kader en Details staan één niveau lager (h3), zoals de spine ze ten opzichte van de naam ordent. */}
        <h2 className="text-[1.25rem] font-bold tracking-[-0.02em] text-[var(--foreground-heading)] [overflow-wrap:anywhere]" data-testid="werkkwaliteit-persoon-kop">
          {volledig ?? t('qa.medewerker.kopZonderNaam')}
        </h2>
        {!geweigerd && <PeriodPicker onKies={kies} waarde={week} weken={weken} />}
      </div>

      {geweigerd ? <Geweigerd /> : <Medewerkerinhoud key={userId} naam={naam} nummer={nummer} week={week} weken={weken} />}
    </div>
  );
}
